/**
 * The platform steps, written once.
 *
 * ── Why these are shared and the rest of a guide is not ──────────────────────
 *
 * Because they are the same for every app, and that is precisely the trap this
 * corpus has to avoid. If each page carried its own copy of "Settings → Screen
 * Time → App Limits", fifteen pages would be fifteen near-identical documents
 * differing in one noun — which is the definition of the thin, templated content
 * Google's scaled-content policy names, and it would be true rather than unfair.
 *
 * Sharing them makes the difference between the pages the part that is actually
 * different: the app's own design, and what it does to a person. It also means
 * an iOS menu change is one edit here rather than fifteen chances to leave a
 * stale instruction on a live page.
 *
 * Current as of iOS 18 and the Digital Wellbeing shipped on Android 14/15. The
 * menu names below are Apple's and Google's own labels.
 */

/** Apple's Screen Time limit, which is the thing everybody tries first. */
export const IOS_APP_LIMIT: string[] = [
  "Open Settings, then Screen Time.",
  "Tap App Limits, then Add Limit.",
  "Choose the app — or its whole category, which is usually the better choice, because a category covers the alternative you will open instead.",
  "Set the daily amount, tap Add.",
  "Turn on Block at End of Limit if you want the limit to be more than a suggestion, and set a Screen Time passcode that is not your unlock code.",
];

/** Downtime, which is the schedule rather than the budget. */
export const IOS_DOWNTIME: string[] = [
  "Settings, then Screen Time, then Downtime.",
  "Turn it on and set the hours — for most people the useful window is the last two hours of the evening, not the middle of the day.",
  "Under Always Allowed, remove everything you do not genuinely need during those hours. Phone calls and messages stay by default.",
  "Turn on Block at Downtime, or the schedule is a notification rather than a boundary.",
];

/** Google's equivalent, which is a timer rather than a block. */
export const ANDROID_TIMER: string[] = [
  "Open Settings, then Digital Wellbeing & parental controls.",
  "Tap Dashboard, find the app, and tap the hourglass next to it.",
  "Set a daily timer. The app's icon greys out when the timer runs out and it stays that way until midnight.",
  "The timer can be removed in the same three taps, which is the whole difficulty with it.",
];

/**
 * The sentence every guide has to carry about Android.
 *
 * Kept here rather than typed per page so it cannot be softened on one page and
 * not another, and so it is one edit on the day the Android build ships. The
 * site has already claimed an Android app that was not there once; fifteen new
 * pages repeating that claim would be the same mistake at fifteen times the
 * scale.
 */
export const ANDROID_CAVEAT =
  "Bible Shield is iPhone and iPad only today — the blocking is built on Apple's Screen Time, and the Android build is not published yet. So everything on this page about Android is Google's own Digital Wellbeing rather than anything we make.";
